import { useCallback, useLayoutEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { LocateView } from '../../state/locate';
import { newLandingState, showWhereHref } from '../../state/show-where';
import {
  NO_FLOOR_TAGS,
  floorTagsOf,
  sameFloorTags,
  type FloorTagGroups,
} from '../../state/attention/floor-tags';
import type { AttentionSubject } from '../../state/attention/select';
import { useActiveSite } from '../../state/useActiveSite';
import { AttentionColumn } from './attention/AttentionColumn';
import { VerdictBar } from './attention/VerdictBar';
import { attentionFocus, attentionPanel } from './attention/attention-actions';
import { attentionColumnShown } from './attention/attention-column';
import { OverviewFloorTagsContext } from './attention/floor-tags-context';
import { useAttentionEnabled, useAttentionModel } from './attention/useAttentionModel';
import type { CalloutOpen } from './overview-landing';
import './OverviewFrame.css';

/**
 * The Overview's frame (twin slice 1, plan W5 step 14): the verdict bar
 * over the live view, and the attention column beside it, in Map and in
 * Building alike.
 *
 * MOUNTED ONCE. The frame hosts both views: the view on screen is its
 * child, and the verdict bar and the column are its own, from ONE
 * `useAttentionModel` -- one debounce, one list -- so flipping the view
 * never remounts the list, restarts a dwell or rewords an item. On an ops
 * 1180 x 820 window both views therefore carry the same words.
 *
 * BEHIND THE TWO FLAGS (contract §8): ops audiences read `attentionEnabled`,
 * site audiences -- `foh_operator`, `site_supervisor`, `service_operator`
 * (A2) and a login with no known role -- `attentionForSiteStaff`; both
 * default off. While this viewer's flag is off nothing is mounted: no bar,
 * no column, no space held, nothing polled (`useAttentionEnabled` is the
 * flag alone), and no card's words -- "Nexsoss may not know. Please call."
 * among them -- reach the page.
 *
 * A CARD'S TWO BUTTONS. "Show where" is a link: the Overview's own locate
 * push, in the view on screen, `from=attention` (`attention-actions.ts`).
 * It may move the floor and ring the subject; it never opens a drawer.
 * "Open <name> panel" is the person's own tap, through the host's handler
 * (`onOpenPanel`), as the callout's button is.
 *
 * THE COLUMN ONLY WHEN SOMETHING IS WRONG (2026-10-06). The column is
 * on screen only while the verdict is not "All clear" and not still
 * checking (`attentionColumnShown`): a card someone acts on, a site row,
 * "Can't check", reconnecting, fire, other open alerts, or "Looks fixed"
 * (kept up so a card that is clearing does not jump the view twice). The
 * model stays mounted either way -- the bar, the floor tags and every
 * dwell run on -- so the column coming back loses nothing.
 *
 * THE RAIL. While the column is on screen it carries `nx-compact-nav`: the
 * shell draws its nav as the 64 px rail below 1280 px, as it does below
 * 1024 px for every screen, so the view keeps its width beside the 400 px
 * column (`OverviewFrame.css`).
 *
 * THE FLOOR TAGS. The Building view's floor list tags each floor a card
 * that is up names, with who acts (`floorTagsOf`). They come from the same
 * model as the column, handed down to the view through a context the frame
 * provides around it (`floor-tags-context.ts`): the view is rendered by
 * the Overview, above this frame, so a prop cannot carry them, and a
 * second model would be a second debounce. The model publishes them to the
 * frame before the page paints, so a floor is never a frame behind its
 * card. While this viewer's flag is off no floor is tagged, whatever was
 * last published. The provider stands in the same place either way, so
 * the flag coming or going never remounts the view.
 */
export interface OverviewFrameProps {
  /** The view on screen: where a card's "Show where" lands. */
  readonly view: LocateView;
  /** The host's handler for a panel, as the callout's button calls it. */
  readonly onOpenPanel: (open: CalloutOpen) => void;
  /** The live view: its heading, controls, callout and canvas. */
  readonly children: ReactNode;
}

export function OverviewFrame({ view, onOpenPanel, children }: OverviewFrameProps) {
  const attention = useAttentionEnabled();
  const [floorTags, setFloorTags] = useState<FloorTagGroups>(NO_FLOOR_TAGS);
  // Same floors, same groups: the map the view already has, so a new card
  // on a floor already tagged re-renders no floor list.
  const publishFloorTags = useCallback(
    (next: FloorTagGroups) => setFloorTags((prev) => (sameFloorTags(prev, next) ? prev : next)),
    [],
  );
  return (
    <div className="nx-overview-frame" data-testid="overview-frame">
      {/* Ahead of the view, and the view always in the same place, so the
          list coming or going never remounts it. */}
      {attention ? (
        <OverviewAttention view={view} onOpenPanel={onOpenPanel} onFloorTags={publishFloorTags} />
      ) : null}
      <OverviewFloorTagsContext.Provider value={attention ? floorTags : NO_FLOOR_TAGS}>
        <div className="nx-overview-frame__main">{children}</div>
      </OverviewFloorTagsContext.Provider>
    </div>
  );
}

interface OverviewAttentionProps extends Omit<OverviewFrameProps, 'children'> {
  /** Where the model's floor tags go: up to the frame, which hands them
   *  down to the view. */
  readonly onFloorTags: (tags: FloorTagGroups) => void;
}

/** The verdict bar and the column, from the one attention model. */
function OverviewAttention({ view, onOpenPanel, onFloorTags }: OverviewAttentionProps) {
  const attention = useAttentionModel();
  const navigate = useNavigate();
  const siteId = useActiveSite().site?.id ?? null;
  const model = attention?.model ?? null;
  const floorTags = useMemo(() => (model ? floorTagsOf(model) : NO_FLOOR_TAGS), [model]);
  // Before paint: the floor list never shows a floor a frame behind its card.
  useLayoutEffect(() => onFloorTags(floorTags), [floorTags, onFloorTags]);
  if (!attention) return null;

  const showWhere = (subject: AttentionSubject) => {
    const focus = attentionFocus(subject);
    const href = focus ? showWhereHref(siteId, view, focus, { kind: 'attention' }) : null;
    // A PUSH with a fresh landing id, as a toast's "Show where" pushes it.
    if (href) navigate(href, { state: newLandingState() });
  };
  const openPanel = (subject: AttentionSubject) => {
    const open = attentionPanel(subject);
    if (open) onOpenPanel(open);
  };

  return (
    <>
      <div className="nx-overview-frame__verdict" data-testid="overview-frame-verdict">
        <VerdictBar model={attention.model} />
      </div>
      {attentionColumnShown(attention.model) ? (
        <div className="nx-overview-frame__column nx-compact-nav" data-testid="overview-frame-column">
          <AttentionColumn
            model={attention.model}
            clock={attention.clock}
            rightNow={attention.rightNow}
            onShowWhere={showWhere}
            onOpenPanel={openPanel}
          />
        </div>
      ) : null}
    </>
  );
}
